import { useContext } from 'react';
import { View, Text, ActivityIndicator, StyleSheet } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { StackScreen, ADSContext } from '@banhala/ui-kit';
import { AtomicItemListType, AtomicHeaderType } from 'shared/src/api';
import {
  ProfileInfo,
  Divider,
  GoodsCarousel,
  TwoColGoodsList,
  ThreeColGoodsList,
  TwoLineMessage,
  Empty,
  NotificationGuide,
  CompanyInfo,
  SearchBar,
  NeonSignBoard,
  Summary,
  HorizontalChipList,
  FourColIconList,
  FiveColIconList,
  ScrollableIconList,
  MarketBadgeList,
  StoreList,
  BannerCarousel,
  DescriptionCardCarousel,
  PhotoReviewCarousel,
  VerticalBannerList,
} from 'shared/src/component/AtomicRenderer/ItemList';
import { FilterList } from 'shared/src/component/AtomicRenderer/ItemList/FilterList';
import {
  HeadlineSmallOneLine,
  HeadlineXsmallOneLine,
  HeadlineSmallOneLineWithText,
  HeadlineSmallOneLineWithTextCompact,
  HeadlineOnlyText,
  HeadlineSmallTwoLineWithTimer,
} from 'shared/src/component/AtomicRenderer/Header';
import { useGetComponentData } from './useGetComponentData';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ItemListComponent: Record<string, React.ComponentType<any>> = {
  [AtomicItemListType.PROFILE_INFO]: ProfileInfo,
  [AtomicItemListType.Divider]: Divider,
  [AtomicItemListType.GoodsCarousel]: GoodsCarousel,
  [AtomicItemListType.FilterList]: FilterList,
  [AtomicItemListType.TwoColGoodsList]: TwoColGoodsList,
  [AtomicItemListType.TwoLineMessage]: TwoLineMessage,
  [AtomicItemListType.Empty]: Empty,
  [AtomicItemListType.NotificationGuide]: NotificationGuide,
  [AtomicItemListType.CompanyInfo]: CompanyInfo,
  [AtomicItemListType.SearchBar]: SearchBar,
  [AtomicItemListType.NeonSignBoard]: NeonSignBoard,
  [AtomicItemListType.Summary]: Summary,
  [AtomicItemListType.HorizontalChipList]: HorizontalChipList,
  [AtomicItemListType.FourColIconList]: FourColIconList,
  [AtomicItemListType.FiveColIconList]: FiveColIconList,
  [AtomicItemListType.ScrollableIconList]: ScrollableIconList,
  [AtomicItemListType.MarketBadgeList]: MarketBadgeList,
  [AtomicItemListType.ThreeColGoodsList]: ThreeColGoodsList,
  [AtomicItemListType.StoreList]: StoreList,
  [AtomicItemListType.BannerCarousel]: BannerCarousel,
  [AtomicItemListType.DescriptionCardCarousel]: DescriptionCardCarousel,
  [AtomicItemListType.PhotoReviewCarousel]: PhotoReviewCarousel,
  [AtomicItemListType.VerticalBannerList]: VerticalBannerList,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HeaderComponent: Record<string, React.ComponentType<any>> = {
  [AtomicHeaderType.HeadlineSmallOneLine]: HeadlineSmallOneLine,
  [AtomicHeaderType.HeadlineXsmallOneLine]: HeadlineXsmallOneLine,
  [AtomicHeaderType.HeadlineSmallOneLineWithText]: HeadlineSmallOneLineWithText,
  [AtomicHeaderType.HeadlineSmallOneLineWithTextCompact]:
    HeadlineSmallOneLineWithTextCompact,
  [AtomicHeaderType.HeadlineOnlyText]: HeadlineOnlyText,
  [AtomicHeaderType.HeadlineSmallTwoLineWithTimer]:
    HeadlineSmallTwoLineWithTimer,
};

interface ScreenRendererCoreProps {
  type: string;
}

// Core 컴포넌트 - Navigation 없이 독립적으로 사용 가능
export const ScreenRendererCore = ({ type }: ScreenRendererCoreProps) => {
  const { contentWidth } = useContext(ADSContext);
  const { data, isLoading, error } = useGetComponentData(type);

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>
          Error: {error instanceof Error ? error.message : 'Unknown error'}
        </Text>
      </View>
    );
  }

  if (!data) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>No data available</Text>
      </View>
    );
  }

  const headerType = data.type.header;
  const itemListType = data.type.item_list;

  if (!itemListType) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>No item_list type in component</Text>
      </View>
    );
  }

  const Header = headerType ? HeaderComponent[headerType] : null;
  const Component = ItemListComponent[itemListType];

  if (!Component) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>
          Unsupported component type: {itemListType}
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.content, { width: contentWidth }]}>
      {Header && (
        <Header
          header={
            data.entity.header
              ? {
                  ...data.entity.header,
                  logging: data.entity.header.logging ?? data.logging,
                }
              : null
          }
        />
      )}
      <Component
        entity={data.entity.item_list}
        body={data.entity.body}
        logging={data.logging}
        scrollToFilterList={() => {}}
        likeMap={{}}
        setLikeMap={() => {}}
      />
    </View>
  );
};

interface ScreenRendererRouteParams {
  instanceId: string;
  type: string;
}

// Navigation Stack용 wrapper
export const ScreenRenderer = () => {
  const route = useRoute<{
    key: string;
    name: string;
    params: ScreenRendererRouteParams;
  }>();
  const { type } = route.params;

  return (
    <StackScreen title={`ScreenRenderer: ${type}`}>
      <ScreenRendererCore type={type} />
    </StackScreen>
  );
};

const styles = StyleSheet.create({
  content: {
    alignSelf: 'center',
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  errorText: {
    color: 'red',
    fontSize: 14,
    textAlign: 'center',
    padding: 16,
  },
});
